import type { EntitlementsMap, ProductId } from '../../subscriptions/types';

//---OBJECT-ACTIONS-API-RESP-STARTS---//
export type ModelName =
  "Users"
  | "Assessments"
  | "AssessmentQuestions"
  | "AssessmentSubmission"
  | "Questions"
  | "QuestionResponses"
  | "Payments"
  | "CoachContent"
  | "Notifications"
  | "NguPoints"
  | "PaymentAssignments"
  | "DailyMoodCheckIn"
  | "Organizations"
  | "LessonPlanSession"
  | "SessionSlide"
  | "SessionRecording"
  | "SessionSlideRecording"
  | "LessonPlan";

export type ModelType<T extends ModelName> = T extends 'Users' ? Users :
  T extends 'Assessments' ? Assessments :
  T extends 'AssessmentSubmission' ? AssessmentSubmission :
  T extends 'AssessmentQuestions' ? AssessmentQuestions :
  T extends "PaymentAssignments" ? PaymentAssignments :
  T extends 'Questions' ? Questions :
  T extends 'QuestionResponses' ? QuestionResponses :
  T extends 'Payments' ? Payments :
  T extends 'CoachContent' ? CoachContent :
  T extends 'Notifications' ? Notifications :
  T extends 'NguPoints' ? NguPoints :
  T extends 'DailyMoodCheckIn' ? DailyMoodCheckIn :
  T extends 'Organizations' ? Organizations :
  T extends 'LessonPlan' ? LessonPlanModel :
  T extends 'LessonPlanSession' ? LessonPlanSessionModel :
  T extends 'SessionSlide' ? SessionSlideModel :
  T extends 'SessionRecording' ? SessionRecording :
  never

export interface RelEntity<T extends ModelName = ModelName> {
  id: number;
  str: string;
  _type: T;
  img?: string;
  /** Tenant slug when present (Organizations) — used for cross-subdomain links. */
  slug?: string;
  entity?: Partial<ModelType<T>>;
}

export type ITypeFieldSchema = {
  [K in ModelName]: {
    [fieldName: string]: FieldTypeDefinition;
  };
}

export interface PaginatedResponse<T> {
  count: number;
  offset: number;
  limit: number;
  meta: any;
  error: string | null;
  results: Array<T>
}

export interface ApiListResponse<T extends ModelName> extends PaginatedResponse<ModelType<T>> {
  results: Array<ModelType<T>>
}

export function getProp<T extends ModelName, K extends keyof ModelType<T>>(
  entity: ModelType<T>,
  key: K
): ModelType<T>[K] | null {
  if (key in entity) return entity[key];
  return null;
}

export function restructureAsAllEntities<T extends ModelName>(
  modelName: T,
  entity: Partial<ModelType<T>>
): ModelType<T> {
  const schema = TypeFieldSchema[modelName];
  const result: any = { id: (entity as any).id || 0, _type: modelName };

  Object.entries(schema).forEach(([key, field]) => {
    const value = (entity as any)[key];

    if (field.data_type === 'RelEntity') {
      if (!value) {
        // Skip undefined values
      } else if (Array.isArray(value)) {
        // Transform array of RelEntities
        result[key] = value.map((item) =>
          item.entity ? restructureAsAllEntities(item._type as ModelName, item.entity) : item
        );
      } else if (value.entity) {
        // Transform single RelEntity
        result[key] = value.entity ?
          restructureAsAllEntities(value._type as ModelName, value.entity) :
          value;
      } else {
        result[key] = { id: value.id };
      }
    } else if (value !== undefined) {
      result[key] = value;
    }
  });
  return result as ModelType<T>;
}

//---OBJECT-ACTIONS-API-RESP-ENDS---//


//---OBJECT-ACTIONS-TYPE-CONSTANTS-STARTS---//
export interface FieldTypeDefinition {
  machine: string;
  singular: string;
  plural: string;
  data_type: 'string' | 'number' | 'boolean' | 'object' | 'RelEntity';
  field_type: string;
  cardinality: number | typeof Infinity;
  relationship?: ModelName;
  required: boolean;
  default: string;
  example: string;
  options?: Array<{ label: string; id: string; }>;
}

export const TypeFieldSchema: ITypeFieldSchema = {
  "Users": {
    "email": {
      "machine": "email",
      "singular": "Email",
      "plural": "Emails",
      "field_type": "email",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "username": {
      "machine": "username",
      "singular": "Username",
      "plural": "Usernames",
      "field_type": "text",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "real_name": {
      "machine": "real_name",
      "singular": "Real Name",
      "plural": "Real Names",
      "field_type": "text",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "bio": {
      "machine": "bio",
      "singular": "Bio",
      "plural": "Bios",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "avatar": {
      "machine": "avatar",
      "singular": "Avatar",
      "plural": "Avatars",
      "field_type": "image",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "photo": {
      "machine": "photo",
      "singular": "Photo",
      "plural": "Photos",
      "field_type": "image",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "gender": {
      "machine": "gender",
      "singular": "Gender",
      "plural": "Genders",
      "field_type": "select",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": "",
      "options": [
        {
          "label": "Male",
          "id": "male"
        },
        {
          "label": "Female",
          "id": "female"
        },
        {
          "label": "Rather Not Say",
          "id": "rather_not_say"
        }
      ]
    },
    "ethnicity": {
      "machine": "ethnicity",
      "singular": "Ethnicity",
      "plural": "Ethnicities",
      "field_type": "multiselect",
      "data_type": "object",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": "",
      "options": [
        {
          "label": "Black or African American",
          "id": "african_american"
        },
        {
          "label": "White",
          "id": "caucasian"
        },
        {
          "label": "Native American",
          "id": "american_native"
        },
        {
          "label": "Asian",
          "id": "south_asian"
        },
        {
          "label": "Hispanic or Latino",
          "id": "hispanic_latino"
        },
        {
          "label": "Other",
          "id": "other"
        },
        {
          "label": "Prefer Not to Say",
          "id": "prefer_not_to_say"
        }
      ]
    },
    "birthdate": {
      "machine": "birthdate",
      "singular": "Birthdate",
      "plural": "Birthdates",
      "field_type": "date",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "zip_code": {
      "machine": "zip_code",
      "singular": "Zip Code",
      "plural": "Zip Codes",
      "field_type": "text",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "summary": {
      "machine": "summary",
      "singular": "Summary",
      "plural": "Summaries",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "cover_photo": {
      "machine": "cover_photo",
      "singular": "Cover Photo",
      "plural": "Cover Photos",
      "field_type": "image",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "category_performance_mindset": {
      "machine": "category_performance_mindset",
      "singular": "Performance Mindset Score",
      "plural": "Performance Mindset Scores",
      "field_type": "decimal",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": "4.5"
    },
    "category_emotional_regulation": {
      "machine": "category_emotional_regulation",
      "singular": "Emotional Regulation Score",
      "plural": "Emotional Regulation Scores",
      "field_type": "decimal",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": "4.5"
    },
    "category_confidence": {
      "machine": "category_confidence",
      "singular": "Confidence Score",
      "plural": "Confidence Scores",
      "field_type": "decimal",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": "4.5"
    },
    "category_resilience_motivation": {
      "machine": "category_resilience_motivation",
      "singular": "Resilience & Motivation Score",
      "plural": "Resilience & Motivation Scores",
      "field_type": "decimal",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": "4.5"
    },
    "category_concentration": {
      "machine": "category_concentration",
      "singular": "Concentration Score",
      "plural": "Concentration Scores",
      "field_type": "decimal",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": "4.5"
    },
    "category_leadership": {
      "machine": "category_leadership",
      "singular": "Leadership Score",
      "plural": "Leadership Scores",
      "field_type": "decimal",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": "4.5"
    },
    "category_mental_wellbeing": {
      "machine": "category_mental_wellbeing",
      "singular": "Mental Well-being Score",
      "plural": "Mental Well-being Scores",
      "field_type": "decimal",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": "4.5"
    }
  },
  "Assessments": {
    "title": {
      "machine": "title",
      "singular": "Title",
      "plural": "Titles",
      "field_type": "text",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "questions": {
      "machine": "questions",
      "singular": "Question",
      "plural": "Questions",
      "relationship": "AssessmentQuestions",
      "field_type": "type_reference",
      "data_type": "RelEntity",
      "cardinality": Infinity,
      "default": "",
      "required": true,
      "example": ""
    }
  },
  "AssessmentQuestions": {
    "question": {
      "machine": "question",
      "singular": "Question",
      "plural": "Questions",
      "relationship": "Questions",
      "field_type": "type_reference",
      "data_type": "RelEntity",
      "cardinality": Infinity,
      "default": "",
      "required": true,
      "example": ""
    },
    "order": {
      "machine": "order",
      "singular": "Order",
      "plural": "Orders",
      "field_type": "integer",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "conditions": {
      "machine": "conditions",
      "singular": "Condition",
      "plural": "Conditions",
      "field_type": "json",
      "data_type": "object",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    }
  },
  "Questions": {
    "title": {
      "machine": "title",
      "singular": "Title",
      "plural": "Titles",
      "field_type": "text",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "help_text": {
      "machine": "help_text",
      "singular": "Help Text",
      "plural": "Help Texts",
      "field_type": "text",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "question_category": {
      "machine": "question_category",
      "singular": "Question Category",
      "plural": "Question Categories",
      "field_type": "enum",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": "Performance Mindset, Emotional Regulation, Confidence,Resilience & Motivation, Concentration, Leadership, Mental Well-being",
      "options": [
        {
          "label": "Performance Mindset",
          "id": "performance_mindset"
        },
        {
          "label": "Emotional Regulation",
          "id": "emotional_regulation"
        },
        {
          "label": "Self Belief",
          "id": "confidence"
        },
        {
          "label": "Resilience & Motivation",
          "id": "resilience_motivation"
        },
        {
          "label": "Concentration",
          "id": "concentration"
        },
        {
          "label": "Leadership",
          "id": "leadership"
        },
        {
          "label": "Mental Well-being",
          "id": "mental_wellbeing"
        }
      ]
    },
    "scale": {
      "machine": "scale",
      "singular": "Scale",
      "plural": "Scales",
      "field_type": "enum",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": "percentage,one-to-five,one-to-ten",
      "options": [
        {
          "label": "Percentage",
          "id": "percentage"
        },
        {
          "label": "One-to-five",
          "id": "onetofive"
        },
        {
          "label": "One-to-ten",
          "id": "onetoten"
        }
      ]
    }
  },
  "QuestionResponses": {
    "author": {
      "machine": "author",
      "singular": "Athlete",
      "plural": "Athletes",
      "relationship": "Users",
      "field_type": "user_account",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "assessment_question": {
      "machine": "assessment_question",
      "singular": "Assessment Question",
      "plural": "Assessment Questions",
      "relationship": "AssessmentQuestions",
      "field_type": "type_reference",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "response": {
      "machine": "response",
      "singular": "Response",
      "plural": "Responses",
      "field_type": "integer",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    }
  },
  "Payments": {
    "athlete": {
      "machine": "athlete",
      "singular": "Athlete",
      "plural": "Athletes",
      "relationship": "Users",
      "field_type": "user_account",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "preferred_coach": {
      "machine": "preferred_coach",
      "singular": "Preferred Coach",
      "plural": "Preferred Coaches",
      "relationship": "Users",
      "field_type": "user_account",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "paid": {
      "machine": "paid",
      "singular": "Paid",
      "plural": "Payments",
      "field_type": "decimal",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "status": {
      "machine": "status",
      "singular": "Status",
      "plural": "Statuses",
      "field_type": "enum",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": "paid, order, request, declined",
      "options": [
        {
          "label": "Paid",
          "id": "paid"
        },
        {
          "label": "Order",
          "id": "order"
        },
        {
          "label": "Request",
          "id": "request"
        },
        {
          "label": "Declined",
          "id": "declined"
        }
      ]
    },
    "subscription_ends": {
      "machine": "subscription_ends",
      "singular": "Subscription End",
      "plural": "Subscription Ends",
      "field_type": "date",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "purchased_entitlements": {
      "machine": "purchased_entitlements",
      "singular": "Purchased Entitlement",
      "plural": "Purchased Entitlements",
      "field_type": "json",
      "data_type": "object",
      "cardinality": Infinity,
      "default": "",
      "required": false,
      "example": ""
    }
  },
  "PaymentAssignments": {
    "payment": {
      "machine": "payment",
      "singular": "Payment",
      "plural": "Payments",
      "relationship": "Payments",
      "field_type": "type_reference",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "athlete": {
      "machine": "athlete",
      "singular": "Athlete",
      "plural": "Athletes",
      "relationship": "Users",
      "field_type": "user_account",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "coaches": {
      "machine": "coaches",
      "singular": "Coach",
      "plural": "Coaches",
      "relationship": "Users",
      "field_type": "user_account",
      "data_type": "RelEntity",
      "cardinality": Infinity,
      "default": "",
      "required": false,
      "example": ""
    },
    "parents": {
      "machine": "parents",
      "singular": "Guardian",
      "plural": "Guardians",
      "relationship": "Users",
      "field_type": "user_account",
      "data_type": "RelEntity",
      "cardinality": Infinity,
      "default": "",
      "required": false,
      "example": ""
    },
    "pre_assessment_submitted_at": {
      "machine": "pre_assessment_submitted_at",
      "singular": "Pre-Assessment Submitted At",
      "plural": "Pre-Assessment Submitted At",
      "field_type": "datetime",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "post_assessment_submitted_at": {
      "machine": "post_assessment_submitted_at",
      "singular": "Post-Assessment Submitted At",
      "plural": "Post-Assessment Submitted At",
      "field_type": "datetime",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "entitlements": {
      "machine": "entitlements",
      "singular": "Entitlement",
      "plural": "Entitlements",
      "field_type": "json",
      "data_type": "object",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    }
  },
  "CoachContent": {
    "author": {
      "machine": "author",
      "singular": "Coach",
      "plural": "Coaches",
      "relationship": "Users",
      "field_type": "user_account",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "title": {
      "machine": "title",
      "singular": "Title",
      "plural": "Titles",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "body": {
      "machine": "body",
      "singular": "Body",
      "plural": "Bodies",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "screenshot_light": {
      "machine": "screenshot_light",
      "singular": "Screenshot Light",
      "plural": "Screenshot Lights",
      "field_type": "image",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "screenshot_dark": {
      "machine": "screenshot_dark",
      "singular": "Screenshot Dark",
      "plural": "Screenshot Darks",
      "field_type": "image",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "prompt": {
      "machine": "prompt",
      "singular": "Prompt",
      "plural": "Prompts",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "assessment": {
      "machine": "assessment",
      "singular": "Assessment",
      "plural": "Assessments",
      "relationship": "Assessments",
      "field_type": "type_reference",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
  },
  "Notifications": {
    "recipient": {
      "machine": "recipient",
      "singular": "Recipient",
      "plural": "Recipients",
      "relationship": "Users",
      "field_type": "user_account",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "message": {
      "machine": "message",
      "singular": "Message",
      "plural": "Messages",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "channel": {
      "machine": "channel",
      "singular": "Channel",
      "plural": "Channels",
      "field_type": "enum",
      "data_type": "string",
      "cardinality": 1,
      "default": "dashboard",
      "required": true,
      "example": "dashboard",
      "options": [
        { "label": "Dashboard", "id": "dashboard" },
        { "label": "Email", "id": "email" },
        { "label": "SMS", "id": "sms" }
      ]
    },
    "delivery_status": {
      "machine": "delivery_status",
      "singular": "Delivery Status",
      "plural": "Delivery Statuses",
      "field_type": "enum",
      "data_type": "string",
      "cardinality": 1,
      "default": "pending",
      "required": true,
      "example": "pending",
      "options": [
        { "label": "Pending", "id": "pending" },
        { "label": "Sent", "id": "sent" },
        { "label": "Delivered", "id": "delivered" },
        { "label": "Failed", "id": "failed" },
        { "label": "Bounced", "id": "bounced" }
      ]
    },
    "seen": {
      "machine": "seen",
      "singular": "Seen (deprecated)",
      "plural": "Seen (deprecated)",
      "field_type": "checkbox",
      "data_type": "boolean",
      "cardinality": 1,
      "default": "false",
      "required": true,
      "example": "false"
    },
    "seen_at": {
      "machine": "seen_at",
      "singular": "Seen At",
      "plural": "Seen At",
      "field_type": "datetime",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": "2026-01-01T12:00:00Z"
    },
    "used_at": {
      "machine": "used_at",
      "singular": "Used At",
      "plural": "Used At",
      "field_type": "datetime",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": "2026-01-01T12:00:00Z"
    },
    "priority": {
      "machine": "priority",
      "singular": "Priority",
      "plural": "Priorities",
      "field_type": "enum",
      "data_type": "string",
      "cardinality": 1,
      "default": "normal",
      "required": true,
      "example": "normal",
      "options": [
        { "label": "Low", "id": "low" },
        { "label": "Normal", "id": "normal" },
        { "label": "High", "id": "high" },
        { "label": "Urgent", "id": "urgent" }
      ]
    }
  },
  "NguPoints": {
    "n_points": {
      "machine": "n_points",
      "singular": "Points",
      "plural": "Points",
      "field_type": "number",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": "100"
    },
    "ngu_event": {
      "machine": "ngu_event",
      "singular": "Event",
      "plural": "Events",
      "field_type": "text",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "scope": {
      "machine": "scope",
      "singular": "Scope",
      "plural": "Scopes",
      "field_type": "text",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "created_at": {
      "machine": "created_at",
      "singular": "Created At",
      "plural": "Created At",
      "field_type": "datetime",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    }
  },
  "AssessmentSubmission": {
    "assignment": {
      "machine": "assessment",
      "singular": "Assessment",
      "plural": "Assessments",
      "relationship": "Assessments",
      "field_type": "type_reference",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "submitted_at": {
      "machine": "submitted_at",
      "singular": "Submitted At",
      "plural": "Submitted At",
      "field_type": "date",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    }
  },
  "DailyMoodCheckIn": {
    "user": {
      "machine": "user",
      "singular": "User",
      "plural": "Users",
      "relationship": "Users",
      "field_type": "user_account",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "mood_choice": {
      "machine": "mood_choice",
      "singular": "Mood Choice",
      "plural": "Mood Choices",
      "field_type": "enum",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": "okay_steady",
      "options": [
        { "label": "I'm okay and steady.", "id": "okay_steady" },
        { "label": "I feel calm and relieved.", "id": "calm_relieved" },
        { "label": "I feel strong and confident.", "id": "strong_confident" },
        { "label": "I feel energized and driven.", "id": "energized_driven" },
        { "label": "I feel centered and in control.", "id": "centered_control" }
      ]
    },
    "check_in_date": {
      "machine": "check_in_date",
      "singular": "Check-in Date",
      "plural": "Check-in Dates",
      "field_type": "date",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": "2025-01-15"
    }
  },
  "Organizations": {
    "name": {
      "machine": "name",
      "singular": "Name",
      "plural": "Names",
      "field_type": "text",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    }
  },
  "LessonPlan": {
    "assignment": {
      "machine": "assignment",
      "singular": "Program Assignment",
      "plural": "Program Assignments",
      "relationship": "PaymentAssignments",
      "field_type": "type_reference",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "submission": {
      "machine": "submission",
      "singular": "Assessment Submission",
      "plural": "Assessment Submissions",
      "relationship": "AssessmentSubmission",
      "field_type": "type_reference",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "athlete": {
      "machine": "athlete",
      "singular": "Athlete",
      "plural": "Athletes",
      "relationship": "Users",
      "field_type": "type_reference",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "notes": {
      "machine": "notes",
      "singular": "Notes",
      "plural": "Notes",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "marked_complete_at": {
      "machine": "marked_complete_at",
      "singular": "Marked Complete At",
      "plural": "Marked Complete At",
      "field_type": "datetime",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "coach_delivered": {
      "machine": "coach_delivered",
      "singular": "Coach Delivered At",
      "plural": "Coach Delivered At",
      "field_type": "datetime",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "athlete_received": {
      "machine": "athlete_received",
      "singular": "Athlete Received At",
      "plural": "Athlete Received At",
      "field_type": "datetime",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "parent_received": {
      "machine": "parent_received",
      "singular": "Guardian Received At",
      "plural": "Guardian Received At",
      "field_type": "datetime",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    }
  },
  "LessonPlanSession": {
    "lesson_plan": {
      "machine": "lesson_plan",
      "singular": "Lesson Plan",
      "plural": "Lesson Plans",
      "relationship": "LessonPlan",
      "field_type": "type_reference",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "session_number": {
      "machine": "session_number",
      "singular": "Session Number",
      "plural": "Session Numbers",
      "field_type": "integer",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "topic": {
      "machine": "topic",
      "singular": "Topic",
      "plural": "Topics",
      "field_type": "text",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "objective": {
      "machine": "objective",
      "singular": "Objective",
      "plural": "Objectives",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "theme": {
      "machine": "theme",
      "singular": "Theme",
      "plural": "Themes",
      "field_type": "text",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "key_tools": {
      "machine": "key_tools",
      "singular": "Key Tools",
      "plural": "Key Tools",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "started_at": {
      "machine": "started_at",
      "singular": "Started At",
      "plural": "Started At",
      "field_type": "datetime",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "completed_at": {
      "machine": "completed_at",
      "singular": "Completed At",
      "plural": "Completed At",
      "field_type": "datetime",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    }
  },
  "SessionSlide": {
    "session": {
      "machine": "session",
      "singular": "Session",
      "plural": "Sessions",
      "relationship": "LessonPlanSession",
      "field_type": "type_reference",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "slide_number": {
      "machine": "slide_number",
      "singular": "Slide Number",
      "plural": "Slide Numbers",
      "field_type": "integer",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "title": {
      "machine": "title",
      "singular": "Title",
      "plural": "Titles",
      "field_type": "text",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "type": {
      "machine": "type",
      "singular": "Type",
      "plural": "Types",
      "field_type": "select",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": "",
      "options": [
        { "label": "Activity", "id": "activity" },
        { "label": "Worksheet", "id": "worksheet" },
        { "label": "Conversation", "id": "conversation" }
      ]
    },
    "body": {
      "machine": "body",
      "singular": "Body",
      "plural": "Bodies",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "script_open": {
      "machine": "script_open",
      "singular": "Script Open",
      "plural": "Script Opens",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "script_on_neutral": {
      "machine": "script_on_neutral",
      "singular": "Script On Neutral",
      "plural": "Script On Neutral",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "script_on_negative": {
      "machine": "script_on_negative",
      "singular": "Script On Negative",
      "plural": "Script On Negative",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "script_on_positive": {
      "machine": "script_on_positive",
      "singular": "Script On Positive",
      "plural": "Script On Positive",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "coaching_tip": {
      "machine": "coaching_tip",
      "singular": "Coaching Tip",
      "plural": "Coaching Tips",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "started_at": {
      "machine": "started_at",
      "singular": "Started At",
      "plural": "Started At",
      "field_type": "datetime",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "completed_at": {
      "machine": "completed_at",
      "singular": "Completed At",
      "plural": "Completed At",
      "field_type": "datetime",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "time_spent_seconds": {
      "machine": "time_spent_seconds",
      "singular": "Time Spent (seconds)",
      "plural": "Time Spent (seconds)",
      "field_type": "integer",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    }
  },
  "SessionRecording": {
    "lesson_plan": {
      "machine": "lesson_plan",
      "singular": "Lesson Plan",
      "plural": "Lesson Plans",
      "relationship": "LessonPlan",
      "field_type": "type_reference",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": ""
    },
    "session": {
      "machine": "session",
      "singular": "Session",
      "plural": "Sessions",
      "relationship": "LessonPlanSession",
      "field_type": "type_reference",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "slide": {
      "machine": "slide",
      "singular": "Slide",
      "plural": "Slides",
      "relationship": "SessionSlide",
      "field_type": "type_reference",
      "data_type": "RelEntity",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "recording_type": {
      "machine": "recording_type",
      "singular": "Recording Type",
      "plural": "Recording Types",
      "field_type": "select",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": true,
      "example": "",
      "options": [
        { "label": "Lesson Plan", "id": "lesson_plan" },
        { "label": "Session", "id": "session" },
        { "label": "Slide", "id": "slide" }
      ]
    },
    "recording": {
      "machine": "recording",
      "singular": "Recording",
      "plural": "Recordings",
      "field_type": "file",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "duration": {
      "machine": "duration",
      "singular": "Duration",
      "plural": "Durations",
      "field_type": "integer",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "file_size": {
      "machine": "file_size",
      "singular": "File Size",
      "plural": "File Sizes",
      "field_type": "integer",
      "data_type": "number",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "mime_type": {
      "machine": "mime_type",
      "singular": "MIME Type",
      "plural": "MIME Types",
      "field_type": "text",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "notes": {
      "machine": "notes",
      "singular": "Notes",
      "plural": "Notes",
      "field_type": "textarea",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "started_at": {
      "machine": "started_at",
      "singular": "Started At",
      "plural": "Started At",
      "field_type": "datetime",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    },
    "ended_at": {
      "machine": "ended_at",
      "singular": "Ended At",
      "plural": "Ended At",
      "field_type": "datetime",
      "data_type": "string",
      "cardinality": 1,
      "default": "",
      "required": false,
      "example": ""
    }
  },
  "SessionSlideRecording": {}
}
//---OBJECT-ACTIONS-TYPE-CONSTANTS-ENDS---//

//---OBJECT-ACTIONS-TYPE-SCHEMA-STARTS---//
export interface SuperModel {
  readonly id: number;
  author: RelEntity<'Users'>;
  created_at: string;
  modified_at: string;
  _type: ModelName;
}

export interface Users {
  readonly id: number
  _type: string
  is_active?: boolean
  is_staff?: boolean
  last_login?: string
  date_joined?: string
  first_name?: string
  last_name?: string
  groups?: string[]
  email: string;
  username: string;
  real_name?: string | null;
  bio?: string | null;
  summary?: string | null;
  avatar?: string | null;
  photo?: string | null;
  cover_photo?: string | null;
  gender?: string | null;
  ethnicity?: string[] | null;
  birthdate?: string | null;
  zip_code?: string | null;
  emergency_contact?: string | null;

  category_performance_mindset?: number | null;
  category_emotional_regulation?: number | null;
  category_confidence?: number | null;
  category_resilience_motivation?: number | null;
  category_concentration?: number | null;
  category_leadership?: number | null;
  category_mental_wellbeing?: number | null;
  category_communication?: number | null;
  category_total_score?: number | null;
}

// Context API types
export interface OrganizationPublicData {
  id: number;
  name: string;
  short_name?: string | null;
  slug: string;
  is_active: boolean;
  logo?: string | null;
  logo_dark?: string | null;
  logo_icon?: string | null;
  logo_icon_dark?: string | null;
  branding_palette?: any;
  branding_typography?: any;
  contact_email?: string | null;
  contact_phone?: string | null;
  /** Athlete waiver markdown filename. */
  waiver_athlete?: string;
  /** Adult (coach/guardian) waiver markdown filename. */
  waiver_adult?: string;
  /** @deprecated use waiver_athlete */
  waiver_name?: string;
  /** Role display labels (athlete, coach, parent). Defaults: Athlete, Coach, Guardian. */
  role_labels?: RoleLabelsByKey;
}

/** Role display labels by machine name. Defaults: Athlete, Coach, Guardian. */
export interface RoleLabelsByKey {
  athlete: string;
  coach: string;
  parent: string;
}

export interface UserOrganizationMembership {
  id: number;  // UserOrganizations ID
  roles: USER_TYPE[];  // user's roles within this organization based on PaymentAssignments
  is_admin: boolean;
  joined_at: string;
  is_active: boolean;
}

export interface DirectoryMemberCategoryScores {
  performance_mindset: number | null;
  emotional_regulation: number | null;
  confidence: number | null;
  resilience_motivation: number | null;
  concentration: number | null;
  leadership: number | null;
  mental_wellbeing: number | null;
  communication: number | null;
}

export interface DirectoryMember {
  id: number;
  uo_id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  username: string;
  avatar: string | null;
  roles: USER_TYPE[];
  is_admin: boolean;
  joined_at: string | null;
  category_total_score: number | null;
  category_scores: DirectoryMemberCategoryScores;
  has_pre_assessment: boolean;
  has_post_assessment: boolean;
  program_start_date: string | null;
  session_completion_pct: number;
  session_expected_pct: number | null;
  session_on_track: boolean | null;
}

export type LessonPlanRole = 'payer' | 'athlete' | 'coach' | 'parent';

export type PurposeChoice =
  | 'lesson_plan'
  | 'curriculum'
  | 'talking_points'
  | 'feedback_report'
  | 'growth_report_week_12'
  | 'growth_report_week_4'
  | 'growth_report_week_8';

// Progress tracking interfaces - matches PurposeChoice
export interface ProgressTracking {
  lesson_plan: RelEntity<'CoachContent'>[] | null;
  curriculum: RelEntity<'CoachContent'>[] | null;
  talking_points: RelEntity<'CoachContent'>[] | null;
  feedback_report: RelEntity<'CoachContent'>[] | null;
  growth_report_week_12: RelEntity<'CoachContent'>[] | null;
  growth_report_week_4: RelEntity<'CoachContent'>[] | null;
  growth_report_week_8: RelEntity<'CoachContent'>[] | null;
}

export interface ContentProgressTracking {
  lesson_plan: RelEntity<'CoachContent'>[] | null;
  curriculum: RelEntity<'CoachContent'>[] | null;
  talking_points: RelEntity<'CoachContent'>[] | null;
  feedback_report: RelEntity<'CoachContent'>[] | null;
  growth_report_week_12: RelEntity<'CoachContent'>[] | null;
  growth_report_week_4: RelEntity<'CoachContent'>[] | null;
  growth_report_week_8: RelEntity<'CoachContent'>[] | null;
}

// Lightweight assignment data from admin detail endpoint (/api/org-directory/<uo_id>/detail)
export interface DirectoryAssignment {
  athlete_id: number;
  athlete: RelEntity<'Users'>;
  coaches: RelEntity<'Users'>[];
  parents: RelEntity<'Users'>[];
  payers: RelEntity<'Users'>[];
  my_roles: string[];
  pre_assessment_submitted_at: string | null;
  paymentassignment_id: number;
  payment_ids: number[];
  assessment_id?: number | null;
}

export interface ReminderNotification {
  id: number;
  notification_type: 'assessment-reminder' | 'coach-publish-reminder';
  message: string;
  sent_at: string | null;
}

export interface DirectoryMemberDetail {
  user_id: number;
  uo_id: number;
  assignments: DirectoryAssignment[];
  last_activity_at: string | null;
  last_reminder_notifications: ReminderNotification[];
}

// Athlete-centric payment assignment structure (from /athlete-assignments endpoint)
export interface AthletePaymentAssignment {
  id: number;
  athlete_id: number | null;
  athlete: RelEntity<'Users'> | null;
  coaches: RelEntity<'Users'>[];
  parents: RelEntity<'Users'>[];
  my_roles: LessonPlanRole[];
  organization: RelEntity<'Organizations'>;
  pre_submission_response_count: number;
  pre_assessment_submitted_at: string | null;
  post_assessment_submitted_at: string | null;
  assessment_id: number | null;
  pre_submission: RelEntity<'AssessmentSubmission'> | null;
  post_submissions: Array<RelEntity<'AssessmentSubmission'>>;
  show_post_assessment_step: boolean;
  submissions: Array<RelEntity<'AssessmentSubmission'>>;
  can_submit_post_assessment: boolean;
  last_update_at: string;
  paymentassignment_id: number;
  payment: RelEntity<'Payments'>;
  content_progress: ContentProgressTracking;
  /** Highest completed lesson plan session week (1–12), 0 if none. */
  last_completed_lesson_week?: number;
  /** When the 12-week lesson program began (lesson-start anchored), null if not started. */
  program_start_date?: string | null;
  /** Percent of the 12 sessions completed (0–100). */
  session_completion_pct?: number;
  /** Expected-by-now completion percent based on weekly pace, null without a start date. */
  session_expected_pct?: number | null;
  /** Strict on-track flag (completed >= expected), null without a start date. */
  session_on_track?: boolean | null;
  /** Most recent program activity timestamp, null if none. */
  last_activity_at?: string | null;
  entitlements?: EntitlementsMap;
  /** 'ai' means the assignment runs unattended (AI auto-publishes content). */
  coaching_mode?: 'human' | 'ai';
  /** Summary list payloads omit coach/parent RelEntities but include counts for onboarding. */
  coach_count?: number;
  parent_count?: number;
  waiver_required?: boolean;
  required_waivers?: string[];
  pending_waiver_roles?: string[];
}

// Context API response type
export interface ContextApiResponse {
  organization: OrganizationPublicData | null;
  membership: UserOrganizationMembership | null;
  mood: {
    id: number;
    mood_choice: string;
    check_in_date: string;
    created_at: string;
    modified_at: string;
  } | null;
}

export interface Assessments extends SuperModel {
  title: string;
  questions: RelEntity<"AssessmentQuestions">[];
  description?: string;
  assignment_id?: number;
}

export interface AssessmentData extends SuperModel {
  title: string;
  description?: string;
  questions: Questions[];
  /** 1-based ordinal for this attempt (1 = pre-assessment, 2 = post-assessment, …). */
  submissions_count?: number | null;
  has_human_coach?: boolean;
}

export interface AssessmentQuestions extends SuperModel {
  question: RelEntity<"Questions">[];
  order: number;
  conditions?: object | null;
}

export interface Questions extends SuperModel {
  assessment_question_id?: number;
  title: string;
  help_text?: string | null;
  question_category?: string | null;
  scale?: string | null;
  // Optional custom labels for numeric choices, e.g., {"1": "None of the time", ...}
  scale_choice_labels?: Record<string, string> | null;
  audio_url?: string | null;
}

export interface AssessmentSubmission extends SuperModel {
  assignment: RelEntity<"PaymentAssignments">;
  assessment: RelEntity<"Assessments">;
  submitted_at?: string | null;
  response_count?: number | null; // custom property in injected in `/athlete-assignemnts`
  category_performance_mindset?: number | null;
  category_emotional_regulation?: number | null;
  category_confidence?: number | null;
  category_resilience_motivation?: number | null;
  category_concentration?: number | null;
  category_leadership?: number | null;
  category_mental_wellbeing?: number | null;
  category_communication?: number | null;
  category_total_score?: number | null;
}

export interface QuestionResponses extends SuperModel {
  author: RelEntity<"Users">;
  assessment_question: RelEntity<"AssessmentQuestions">;
  assessment: RelEntity<"Assessments">;
  response: number;
  submission?: RelEntity<"AssessmentSubmission"> | null;
}

export interface Payments extends SuperModel {
  paid: number;
  status: string;
  subscription_ends?: string | null;
  /** Product ids purchased; named for the legacy DB column. */
  purchased_entitlements?: ProductId[];
  stripe_payment_intent_id?: string | null;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
}

export type AssignmentInvitationRole = 'athlete' | 'coach' | 'parent';
export type AssignmentInvitationChannel = 'email' | 'sms';
export type AssignmentInvitationAction = 'pending' | 'accepted' | 'declined' | 'not_me' | 'revoked';

export interface AssignmentInvitation {
  id: number;
  token: string;
  role: AssignmentInvitationRole;
  channel: AssignmentInvitationChannel;
  invitee_email: string | null;
  invitee_phone: string | null;
  invitee_name: string | null;
  masked_destination: string | null;
  invitee_user_exists?: boolean;
  is_resend?: boolean;
  action: AssignmentInvitationAction;
  delivery_status: 'pending' | 'sent' | 'delivered' | 'failed' | 'bounced';
  sent_at: string | null;
  next_resend_available_at?: string | null;
  expires: string | null;
  acted_at: string | null;
  created_at: string;
  _type: 'AssignmentInvitation';
}

/** Lean payer fields on payment.entity.author in assignment list/detail payloads */
export interface PaymentAssignmentPaymentAuthor {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
}

/** payment.entity shape when PaymentAssignments include payer metadata */
export interface PaymentAssignmentPaymentEntity {
  author?: PaymentAssignmentPaymentAuthor;
  created_at?: string;
}

export type PaymentAssignmentPaymentRel = Omit<RelEntity<'Payments'>, 'entity'> & {
  entity?: PaymentAssignmentPaymentEntity;
};

export interface PaymentAssignments extends SuperModel {
  id: number;
  has_question_responses: boolean;
  pre_assessment_submitted: boolean;
  post_assessment_submitted: boolean;
  pre_assessment_submitted_at: string | null;
  post_assessment_submitted_at: string | null;
  created_at: string;
  modified_at: string;
  author: RelEntity<'Users'>;
  payment: PaymentAssignmentPaymentRel;
  athlete: RelEntity<'Users'>;
  organization: RelEntity<'Organizations'>;
  assessment_id?: number | null;
  pre_assessment?: RelEntity<'Assessments'> | null;  // deprecated, use assessment_id
  coaches: RelEntity<'Users'>[];
  parents: RelEntity<'Users'>[];
  pending_invitations?: AssignmentInvitation[];
  entitlements?: EntitlementsMap;
  _type: "PaymentAssignments";
}



export type GrowthReportBenchmarkStatus = 'Met' | 'Approaching' | 'Building';

export interface GrowthReportDomainRow {
  key: string;
  label: string;
  pre: number | null;
  post: number | null;
  delta: number | null;
  benchmark_status: GrowthReportBenchmarkStatus | null;
}

export interface GrowthReportTopDomain {
  key: string;
  label: string;
  delta: number;
}

export interface GrowthReportFacts {
  overall: {
    pre: number | null;
    post: number | null;
    delta: number | null;
    benchmark_status: GrowthReportBenchmarkStatus | null;
  };
  domains: GrowthReportDomainRow[];
  top_domains: GrowthReportTopDomain[];
  lowest_baseline_improved_most?: boolean;
  has_negatives?: boolean;
}

export interface GrowthReportAlignment {
  level: 'High' | 'Moderate' | 'Emerging' | null;
  reason: string;
  signals: Record<string, unknown>;
}

export interface GrowthReportNarrative {
  observer_summary: string;
  overall_interpretation: string;
  top_domains_insight: string;
}

export interface GrowthReportJsonBody {
  facts: GrowthReportFacts;
  observer_feedback: Record<string, unknown>;
  alignment: GrowthReportAlignment;
  narrative: GrowthReportNarrative;
  generated_at: string;
  change_request: string | null;
}

export interface CoachContent extends SuperModel {
  author: RelEntity<"Users">;
  source: 'ai' | 'user' | 'user_modified';
  title: string;
  body: string;
  // Per-purpose JSON payload populated by orchestrators (e.g. GrowthReportJsonBody for purpose='growth_report_week_12').
  // Not all purposes use this field.
  json_body?: GrowthReportJsonBody | Record<string, any> | null;
  prompt?: string | null;
  screenshot_light?: string | null;
  screenshot_dark?: string | null;
  purpose: string;
  coach_delivered: string | null;
  athlete_received: string | null;
  parent_received: string | null;
  submission: RelEntity<"AssessmentSubmission">;
  assignment: RelEntity<"PaymentAssignments">;
  assessment?: RelEntity<"Assessments"> | null;
  athlete?: RelEntity<"Users"> | null;
  /** Submitted submissions for this assignment (oldest first); drives ResponseGraphs pre/post like program-progress. */
  chart_submissions?: Array<RelEntity<"AssessmentSubmission">>;
  my_roles?: LessonPlanRole[];
  source_content?: RelEntity<"CoachContent"> | null;
}


/**
 * New normalized database model for lesson plans.
 * This is the preferred format going forward.
 * Sessions are returned as flat arrays (not RelEntity wrappers) to avoid infinite nesting.
 */
export interface LessonPlanModel extends SuperModel {
  assignment: RelEntity<"PaymentAssignments">;
  submission: RelEntity<"AssessmentSubmission">;
  athlete: RelEntity<"Users">;
  notes: string | null;
  marked_complete_at: string | null;
  coach_delivered: string | null;
  athlete_received: string | null;
  parent_received: string | null;
  my_roles: LessonPlanRole[];
  /** Coaching mode of the linked assignment; 'ai' auto-publishes content. */
  coaching_mode?: 'human' | 'ai';
  /** Whether a human coach is populated on the linked assignment. */
  has_human_coach?: boolean;
  sessions?: LessonPlanSessionModel[];  // Flat array, no RelEntity wrapper, no lesson_plan back-reference
  screenshot_light?: string | null;
  screenshot_dark?: string | null;
}

/**
 * Session model - lesson_plan back-reference is removed to avoid infinite nesting.
 * Slides are returned as flat arrays (not RelEntity wrappers).
 */
export interface LessonPlanSessionModel extends SuperModel {
  // lesson_plan removed to avoid infinite nesting
  session_number: number;
  topic: string;
  objective: string;
  theme?: string;
  key_tools?: string;
  started_at?: string | null;
  completed_at?: string | null;
  slides?: SessionSlideModel[];  // Flat array, no RelEntity wrapper, no session back-reference
}

/**
 * New normalized database model for session slides.
 * This is the preferred format going forward.
 * Session back-reference is removed to avoid infinite nesting.
 */
export interface SessionSlideModel extends SuperModel {
  // session removed to avoid infinite nesting
  slide_number: number;
  title: string;
  type: "activity" | "worksheet" | "conversation";
  body: string;
  script_open: string;
  script_on_neutral: string;
  script_on_negative: string;
  script_on_positive: string;
  coaching_tip: string;
  media_search_query?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  time_spent_seconds?: number | null;
}

export interface Notifications extends SuperModel {
  recipient: RelEntity<"Users">;
  message: string;
  message_text?: string | null;
  message_html?: string | null;
  channel: 'dashboard' | 'email' | 'sms';
  delivery_status: 'pending' | 'sent' | 'delivered' | 'failed' | 'bounced';
  sent_at?: string | null;
  /** @deprecated Use seen_at — null means unread. */
  seen: boolean;
  seen_at?: string | null;
  used_at?: string | null;
  notification_type?: string | null;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  link?: string | null;
  expires?: string | null;
}

/** Valid NGU event type values (must match backend NguPoints.NguEventTypes). */
export enum NguEventType {
  WAIVER_COMPLETED = "WAIVER_COMPLETED",
  MEDIA_RELEASE_ALLOWED = "MEDIA_RELEASE_ALLOWED",
  DIGITAL_CONSENT_ALLOWED = "DIGITAL_CONSENT_ALLOWED",
  EMERGENCY_CONTACT_PROVIDED = "EMERGENCY_CONTACT_PROVIDED",
  PREASSESSMENT_COMPLETED = "PREASSESSMENT_COMPLETED",
  POSTASSESSMENT_COMPLETED = "POSTASSESSMENT_COMPLETED",
  LESSON_PLAN_VIEWED = "LESSON_PLAN_VIEWED",
  LESSON_PLAN_SESSION_COMPLETED = "LESSON_PLAN_SESSION_COMPLETED",
  LESSON_PLAN_SLIDE_COMPLETED = "LESSON_PLAN_SLIDE_COMPLETED",
  LESSON_RECORDING = "LESSON_RECORDING",
  MOOD_CHECKIN_COMPLETED = "MOOD_CHECKIN_COMPLETED",
  STREAK_BONUS_DAILY_FEELING = "STREAK_BONUS_DAILY_FEELING",
  STREAK_BONUS_WEEKLY_LESSON = "STREAK_BONUS_WEEKLY_LESSON",
}

/** Human-readable labels for each NGU event (matches backend NguEventTypes labels). */
export const NGU_EVENT_LABELS: Record<NguEventType, string> = {
  [NguEventType.WAIVER_COMPLETED]: "Waiver Completed",
  [NguEventType.MEDIA_RELEASE_ALLOWED]: "Media Release Allowed",
  [NguEventType.DIGITAL_CONSENT_ALLOWED]: "Digital Consent Allowed",
  [NguEventType.EMERGENCY_CONTACT_PROVIDED]: "Emergency Contact Provided",
  [NguEventType.PREASSESSMENT_COMPLETED]: "Preassessment Completed",
  [NguEventType.POSTASSESSMENT_COMPLETED]: "Postassessment Completed",
  [NguEventType.LESSON_PLAN_VIEWED]: "Lesson Plan Viewed",
  [NguEventType.LESSON_PLAN_SESSION_COMPLETED]: "Lesson Plan Session Completed",
  [NguEventType.LESSON_PLAN_SLIDE_COMPLETED]: "Lesson Plan Slide Completed",
  [NguEventType.LESSON_RECORDING]: "Lesson Recording",
  [NguEventType.MOOD_CHECKIN_COMPLETED]: "Daily Feeling Check-in Completed",
  [NguEventType.STREAK_BONUS_DAILY_FEELING]: "Streak Bonus Daily Feeling",
  [NguEventType.STREAK_BONUS_WEEKLY_LESSON]: "Streak Bonus Weekly Lesson",
};

/** Base point value for each NGU event (matches backend NguPoints.NGU_EVENT_POINTS). */
export const NGU_EVENT_POINTS: Record<NguEventType, number> = {
  [NguEventType.WAIVER_COMPLETED]: 100,
  [NguEventType.MEDIA_RELEASE_ALLOWED]: 250,
  [NguEventType.DIGITAL_CONSENT_ALLOWED]: 200,
  [NguEventType.EMERGENCY_CONTACT_PROVIDED]: 100,
  [NguEventType.PREASSESSMENT_COMPLETED]: 500,
  [NguEventType.POSTASSESSMENT_COMPLETED]: 500,
  [NguEventType.LESSON_PLAN_VIEWED]: 50,
  [NguEventType.LESSON_PLAN_SESSION_COMPLETED]: 25,
  [NguEventType.LESSON_PLAN_SLIDE_COMPLETED]: 10,
  [NguEventType.LESSON_RECORDING]: 20,
  [NguEventType.MOOD_CHECKIN_COMPLETED]: 100,
  [NguEventType.STREAK_BONUS_DAILY_FEELING]: 100,
  [NguEventType.STREAK_BONUS_WEEKLY_LESSON]: 100,
};

/** NGU points list item (read-only; no author/modified_at). Meta for list response can include grand_total. */
export interface NguPoints {
  id: number;
  n_points: number | null;
  ngu_event: NguEventType | null;
  scope?: string | null;
  created_at: string;
  _type: "NguPoints";
}

export interface DailyMoodCheckIn extends SuperModel {
  user: RelEntity<"Users">;
  mood_choice: 'okay_steady' | 'calm_relieved' | 'strong_confident' | 'energized_driven' | 'centered_control';
  check_in_date: string;
}

export interface Organizations extends SuperModel {
  name: string;
  short_name?: string | null;
  slug: string;
  description?: string;
  is_active: boolean;
  logo?: string | null;
  logo_dark?: string | null;
  logo_icon?: string | null;
  logo_icon_dark?: string | null;
  branding_palette?: any;
  branding_typography?: any;
  contact_email?: string | null;
  contact_phone?: string | null;
}

export interface SessionRecording extends SuperModel {
  lesson_plan: RelEntity<"LessonPlan">;
  session?: RelEntity<"LessonPlanSession"> | null;
  slide?: RelEntity<"SessionSlide"> | null;
  recording_type: 'lesson_plan' | 'session' | 'slide';
  recording?: string | null;
  duration?: number | null;
  file_size?: number | null;
  mime_type?: string | null;
  notes?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
}

export const PurposeNames = {
  lesson_plan: "Lesson Plan",
  curriculum: "Curriculum",
  talking_points: "Talking Points",
  feedback_report: "Feedback Report",
  growth_report_week_12: "Week 12 Growth Report",
  growth_report_week_4: "Week 4 Growth Report",
  growth_report_week_8: "Week 8 Growth Report",
}

//---OBJECT-ACTIONS-TYPE-SCHEMA-ENDS---//

export type USER_TYPE = 'athlete' | 'parent' | 'coach' | 'admin' | 'agent' | 'payer';

// Role configuration with icons, labels, and colors
export const ROLE_CONFIG = {
  athlete: {
    icon: 'Sports',
    label: "Athlete",
    color: "primary" as const,
    description: "Take your assessment and grow with personalized lesson plans"
  },
  parent: {
    icon: 'Diversity3',
    label: "Guardian",
    color: "secondary" as const,
    description: "Observe program progress and offer growth reports along the way"
  },
  coach: {
    icon: 'SportsEsports',
    label: "Coach",
    color: "success" as const,
    description: "Manage all content and optionally guide the lesson plans themselves"
  },
  admin: {
    icon: 'AdminPanelSettings',
    label: "Admin",
    color: "error" as const,
    description: "Full system access to manage users, content, and platform settings"
  },
  agent: {
    icon: 'SmartToy',
    label: "Agent",
    color: "info" as const,
    description: "Agent with specialized access to system functions and data processing"
  },
  payer: {
    icon: 'Payment',
    label: "Payer",
    color: "warning" as const,
    description: "Manage payments and all assignment roles",
  }
} as const;



//---OBJECT-ACTIONS-NAV-ITEMS-STARTS---//
export interface NavItem<T extends ModelName = ModelName> {
  singular: string;
  plural: string;
  segment: string;
  api: string;
  icon: string;
  type: T;
  model_type?: 'vocabulary' | string;
  search_fields: string[];
  roles?: USER_TYPE[];
}

export const NAVITEMS: { [K in ModelName]: NavItem<K> }[ModelName][] = [
  {
    "singular": "User",
    "plural": "Users",
    "type": "Users",
    "segment": "users",
    "api": "/api/users/",
    "icon": "Person",
    "search_fields": [
      "first_name",
      "last_name"
    ],
    "roles": ["admin", "coach", "parent"]
  },
  {
    "singular": "My Assessment",
    "plural": "Assessments",
    "type": "Assessments",
    "segment": "assessments",
    "api": "/api/assessments/",
    "icon": "Quiz",
    "search_fields": [
      "title"
    ],
    "roles": ["athlete"]
  },
  {
    "singular": "Assessment Question",
    "plural": "Assessment Questions",
    "type": "AssessmentQuestions",
    "segment": "assessment-questions",
    "api": "/api/assessment-questions/",
    "icon": "HelpOutline",
    "model_type": "vocabulary",
    "search_fields": [
      "question__title"
    ],
    "roles": ["coach", "admin"]
  },
  {
    "singular": "Question",
    "plural": "Questions",
    "type": "Questions",
    "segment": "questions",
    "api": "/api/questions/",
    "icon": "QuestionMark",
    "model_type": "vocabulary",
    "search_fields": [
      "title"
    ],
    "roles": ["coach", "admin"]
  },
  {
    "singular": "Question Response",
    "plural": "Question Responses",
    "type": "QuestionResponses",
    "segment": "question-responses",
    "api": "/api/question-responses/",
    "icon": "AssignmentTurnedIn",
    "model_type": "vocabulary",
    "search_fields": [
      "question__title"
    ],
    "roles": ["athlete", "coach", "admin"]
  },
  {
    "singular": "Payment",
    "plural": "Payments",
    "type": "Payments",
    "segment": "payments",
    "api": "/api/payments/",
    "icon": "Payment",
    "model_type": "vocabulary",
    "search_fields": [],
    "roles": ["parent", "admin"]
  },
  {
    "singular": "Coach Content",
    "plural": "Coach Content",
    "type": "CoachContent",
    "segment": "coach-content",
    "api": "/api/coach-content/",
    "icon": "Sports",
    "search_fields": [
      "title"
    ],
    "roles": ["athlete", "parent", "coach", "admin"]
  },
  {
    "singular": "Notification",
    "plural": "Notifications",
    "type": "Notifications",
    "segment": "notifications",
    "api": "/api/notifications/",
    "icon": "Notifications",
    "search_fields": [
      "message"
    ],
    "roles": ["athlete", "parent", "coach", "admin"]
  }
]
//---OBJECT-ACTIONS-NAV-ITEMS-ENDS---//
